// CTW-DURMAH-STUDENT-MANUAL-01:
// Single source of truth for the Student Manual.
//
// ONE SOURCE, TWO VIEWS. Every section carries an `audience` tag:
//   - "public": rendered in full on the public /manual pages (indexed, SEO).
//   - "member": rendered in full only to signed-in students. Public visitors
//     see the title + summary and a "Sign in to see the full guide" teaser.
//
// The redaction boundary is governed by docs/STUDENT_MANUAL_REDACTION_PROPOSAL.md
// and was signed off before this file was written. The rule is:
// PUBLISH BEHAVIOUR AND BENEFIT; WITHHOLD MECHANISM AND MAGIC NUMBERS.
//
// HARD RULES for anyone editing this file — enforced by
// scripts/manual/guard-manual-redaction.mjs, which runs in CI-equivalent
// verification and will fail the check:
//   1. NEVER put integrity-refusal trigger keywords or the reproducible
//      refusal string into ANY section, public or member. Publishing the
//      trip-wire teaches students how to route around the guard.
//   2. NEVER put exact operational numbers (character caps, word gates,
//      quotas, rate limits, session ceilings) into a `public` section.
//      They belong in `member` sections only.
//   3. NEVER put internal architecture into any section: route paths beyond
//      the user-facing ones, table names, API endpoints, storage buckets,
//      model names, flag names, or mechanism descriptions.
//
// This module is dependency-free on purpose: it is imported by both the
// browser bundle (manual pages + search) and server code (Durmah grounding).
// Do not add `fs`, `path`, or any server-only import to this tree.

/** Who may see the full body of a section. */
export type ManualAudience = "public" | "member";

/** Top-level grouping on the manual index page. */
export type ManualCategoryId =
  | "getting-started"
  | "your-workspace"
  | "lectures"
  | "durmah"
  | "assignments"
  | "mark-my-paper"
  | "planning"
  | "lexicon"
  | "wellbeing"
  | "community"
  | "account"
  | "integrity"
  | "help";

export interface ManualCategory {
  id: ManualCategoryId;
  label: string;
  /** One line shown under the category heading on the index. */
  blurb: string;
  /** lucide-react icon name, resolved by the page component. */
  icon:
    | "Compass"
    | "LayoutDashboard"
    | "Mic"
    | "FileText"
    | "ClipboardCheck"
    | "CalendarDays"
    | "BookOpen"
    | "Heart"
    | "Users"
    | "Settings"
    | "ShieldCheck"
    | "LifeBuoy"
    | "GraduationCap";
  order: number;
}

/* ------------------------------------------------------------------ */
/* Content blocks                                                      */
/* ------------------------------------------------------------------ */

export interface ManualParagraphBlock {
  kind: "para";
  text: string;
}

export interface ManualHeadingBlock {
  kind: "heading";
  text: string;
  /** Anchor id for in-page navigation. */
  id: string;
}

export interface ManualStepsBlock {
  kind: "steps";
  /** Optional lead-in above the numbered list. */
  intro?: string;
  items: string[];
}

export interface ManualBulletsBlock {
  kind: "bullets";
  intro?: string;
  items: string[];
}

export interface ManualTableBlock {
  kind: "table";
  intro?: string;
  headers: string[];
  rows: string[][];
}

/**
 * A boxed aside. `tone` drives the colour treatment:
 *  - "note"     neutral, supplementary
 *  - "tip"      practical advice that saves the student time
 *  - "boundary" a deliberate limit or refusal — these are trust assets and
 *               should read as confident, not apologetic
 *  - "safety"   wellbeing / crisis / academic-integrity warnings
 */
export interface ManualCalloutBlock {
  kind: "callout";
  tone: "note" | "tip" | "boundary" | "safety";
  title?: string;
  text: string;
}

/** Verbatim product copy the student will actually see on screen. */
export interface ManualQuoteBlock {
  kind: "quote";
  text: string;
  /** e.g. "What Durmah shows you" */
  attribution?: string;
}

export type ManualBlock =
  | ManualParagraphBlock
  | ManualHeadingBlock
  | ManualStepsBlock
  | ManualBulletsBlock
  | ManualTableBlock
  | ManualCalloutBlock
  | ManualQuoteBlock;

/* ------------------------------------------------------------------ */
/* Sections                                                            */
/* ------------------------------------------------------------------ */

export interface ManualSection {
  /** URL slug: /manual/{slug}. Stable — changing it breaks links and SEO. */
  slug: string;
  title: string;
  /** 1–2 sentences. Shown on cards, in search results, and as the meta
   *  description. Also what a public visitor sees for a member-only section. */
  summary: string;
  category: ManualCategoryId;
  audience: ManualAudience;
  /** Float, so sections can be inserted without renumbering. */
  order: number;
  /** Extra search terms a student might use that are not in the title or
   *  body — synonyms, plain-English phrasings, common misspellings. */
  keywords: string[];
  /** Short labels shown as chips. */
  tags: string[];
  readTime: string;
  blocks: ManualBlock[];
  /** Slugs of related sections, rendered as "Keep going" links. */
  related?: string[];
}

/* ------------------------------------------------------------------ */
/* Helpers shared by the UI, the search index and Durmah grounding      */
/* ------------------------------------------------------------------ */

/** Canonical public path for a section. */
export function manualHref(slug: string): string {
  return `/manual/${slug}`;
}

/**
 * Flatten a section's blocks to plain text.
 * Used for the search index and for Durmah's grounded answers, so that both
 * search and the assistant see exactly the words the student sees.
 */
export function sectionPlainText(section: ManualSection): string {
  const parts: string[] = [section.title, section.summary];

  for (const block of section.blocks) {
    switch (block.kind) {
      case "para":
        parts.push(block.text);
        break;
      case "heading":
        parts.push(block.text);
        break;
      case "steps":
      case "bullets":
        if (block.intro) parts.push(block.intro);
        parts.push(...block.items);
        break;
      case "table":
        if (block.intro) parts.push(block.intro);
        parts.push(block.headers.join(" "));
        for (const row of block.rows) parts.push(row.join(" "));
        break;
      case "callout":
        if (block.title) parts.push(block.title);
        parts.push(block.text);
        break;
      case "quote":
        parts.push(block.text);
        break;
    }
  }

  return parts.join(" ");
}

/**
 * The body a given viewer is allowed to see.
 * A member-only section returns no blocks for a signed-out visitor — the page
 * renders the teaser instead. This is the single enforcement point; do not
 * filter audience anywhere else.
 */
export function visibleBlocks(
  section: ManualSection,
  isSignedIn: boolean,
): ManualBlock[] {
  if (section.audience === "member" && !isSignedIn) return [];
  return section.blocks;
}

/** True when the viewer is being shown a teaser rather than the body. */
export function isGated(section: ManualSection, isSignedIn: boolean): boolean {
  return section.audience === "member" && !isSignedIn;
}
